import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldOff, Copy, Check, AlertTriangle } from 'lucide-react';

const Admin2FA = () => {
  const [status, setStatus] = useState({ enabled: false, loading: true });
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableData, setDisableData] = useState({ password: '', code: '' });
  const [message, setMessage] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/2fa/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus({ enabled: data.enabled, loading: false });
    } catch (error) {
      console.error('Error loading 2FA status:', error);
      setStatus({ enabled: false, loading: false });
    }
  };

  const startSetup = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/2fa/setup', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Ошибка настройки 2FA' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения к серверу' });
    }
  };

  const verifySetup = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/2fa/verify-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ code: verifyCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes);
        setSetupData(null);
        setVerifyCode('');
        setStatus({ enabled: true, loading: false });
        setMessage({ type: 'success', text: '2FA успешно активирована!' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Неверный код' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка верификации' });
    }
  };

  const disable2FA = async () => {
    if (!window.confirm('Вы уверены, что хотите отключить 2FA? Это снизит безопасность аккаунта.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/2fa/disable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(disableData)
      });
      
      if (response.ok) {
        setStatus({ enabled: false, loading: false });
        setDisableData({ password: '', code: '' });
        setMessage({ type: 'success', text: '2FA отключена' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Ошибка отключения 2FA' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка подключения' });
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (status.loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
          <Shield className="h-7 w-7" />
          Двухфакторная аутентификация (2FA)
        </h1>
        <p className="text-gray-400 mt-1">
          Дополнительная защита для вашего аккаунта администратора
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Current Status */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {status.enabled ? (
              <div className="p-3 bg-green-600/20 rounded-full">
                <ShieldCheck className="h-8 w-8 text-green-400" />
              </div>
            ) : (
              <div className="p-3 bg-yellow-600/20 rounded-full">
                <ShieldOff className="h-8 w-8 text-yellow-400" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-white">
                {status.enabled ? '2FA активирована' : '2FA не активирована'}
              </h2>
              <p className="text-gray-400 text-sm">
                {status.enabled 
                  ? 'Ваш аккаунт защищён двухфакторной аутентификацией'
                  : 'Рекомендуется включить для дополнительной защиты'
                }
              </p>
            </div>
          </div>
          
          {!status.enabled && !setupData && (
            <button
              onClick={startSetup}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Включить 2FA
            </button>
          )}
        </div>
      </div>

      {/* Backup Codes (shown after setup) */}
      {backupCodes && (
        <div className="bg-yellow-900/30 rounded-xl p-6 border border-yellow-700">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-300">Сохраните резервные коды!</h3>
              <p className="text-yellow-200/80 text-sm">
                Эти коды можно использовать для входа, если вы потеряете доступ к приложению-аутентификатору. 
                Каждый код можно использовать только один раз.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {backupCodes.map((code, index) => (
              <div
                key={index}
                onClick={() => copyToClipboard(code, index)}
                className="bg-gray-800 px-3 py-2 rounded font-mono text-sm text-white flex items-center justify-between cursor-pointer hover:bg-gray-700"
              >
                <span>{code}</span>
                {copiedCode === index ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500" />
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setBackupCodes(null)}
            className="mt-4 text-sm text-yellow-400 hover:text-yellow-300"
          >
            Я сохранил коды, закрыть
          </button>
        </div>
      )}

      {/* Setup Form */}
      {setupData && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-6">
          <h3 className="text-lg font-semibold text-white">Настройка 2FA</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-4">
                1. Отсканируйте QR-код в приложении-аутентификаторе (Google Authenticator, Authy, и т.д.)
              </p>
              <img
                src={setupData.qr_code}
                alt="2FA QR Code"
                className="mx-auto bg-white p-2 rounded-lg"
                style={{ maxWidth: '200px' }}
              />
            </div>
            
            {/* Manual Entry */}
            <div>
              <p className="text-gray-400 text-sm mb-4">
                Или введите ключ вручную:
              </p>
              <div
                onClick={() => copyToClipboard(setupData.secret, 'secret')}
                className="bg-gray-900 p-3 rounded-lg font-mono text-sm text-white flex items-center justify-between cursor-pointer hover:bg-gray-700"
              >
                <span className="break-all">{setupData.secret}</span>
                {copiedCode === 'secret' ? (
                  <Check className="h-4 w-4 text-green-400 flex-shrink-0 ml-2" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500 flex-shrink-0 ml-2" />
                )}
              </div>
              
              <div className="mt-6">
                <p className="text-gray-400 text-sm mb-2">
                  2. Введите код из приложения для подтверждения:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-xl tracking-widest font-mono"
                    maxLength={6}
                  />
                  <button
                    onClick={verifySetup}
                    disabled={verifyCode.length !== 6}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Подтвердить
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setSetupData(null)}
            className="text-gray-400 hover:text-white text-sm"
          >
            Отмена
          </button>
        </div>
      )}

      {/* Disable 2FA */}
      {status.enabled && !setupData && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Отключить 2FA</h3>
          <p className="text-gray-400 text-sm mb-4">
            Для отключения 2FA введите ваш пароль и текущий код из приложения-аутентификатора.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="password"
              value={disableData.password}
              onChange={(e) => setDisableData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Пароль"
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
            <input
              type="text"
              value={disableData.code}
              onChange={(e) => setDisableData(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
              placeholder="Код 2FA или резервный код"
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>
          
          <button
            onClick={disable2FA}
            disabled={!disableData.password || !disableData.code}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Отключить 2FA
          </button>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
        <h4 className="text-blue-300 font-medium mb-2">Рекомендуемые приложения</h4>
        <ul className="text-blue-200/80 text-sm space-y-1">
          <li>• Google Authenticator (iOS / Android)</li>
          <li>• Microsoft Authenticator (iOS / Android)</li>
          <li>• Authy (iOS / Android / Desktop)</li>
        </ul>
      </div>
    </div>
  );
};

export default Admin2FA;
